import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { flushSync } from "react-dom";
import { useI18n, type MessageKey } from "../i18n";
import { checkDateInput, datePattern, formatDateInput, type DateIssue } from "../lib/date-input";
import { Icon } from "./Icon";

const ISSUE_KEY: Record<DateIssue, MessageKey> = {
  invalid: "date.invalid",
  required: "date.required",
  tooEarly: "date.tooEarly",
  tooLate: "date.tooLate",
};

/** Native picker support (Chromium 99+, Safari 16+, Firefox 101+); without it the field is typed only. */
const canShowPicker = typeof HTMLInputElement !== "undefined" && "showPicker" in HTMLInputElement.prototype;
/**
 * Touch screens (ADR 0030): the hidden native date input is laid over the calendar button, so a tap opens
 * the phone's own date wheel or calendar directly, also where showPicker() is missing (older iOS).
 */
export const touchPicker = typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(pointer: coarse)").matches;

/**
 * Date entry that reads and types day first in the console language (TR "02.11.2026", EN "02/11/2026")
 * whatever the browser language, while `value` / `onChange` stay ISO ("2026-11-02"). A calendar button
 * opens the browser's own picker on a hidden native date input, so mouse users keep a calendar.
 * `onChange` fires on blur, Enter or a picker choice, only with a valid, in-range ISO day ("" when cleared and
 * not required); keystrokes alone never call it.
 */
export function DateField({
  id,
  value,
  onChange,
  min,
  max,
  required = false,
  describedBy,
  invalid = false,
  autoFocus = false,
  label,
}: {
  id: string;
  value: string;
  onChange: (iso: string) => void;
  min?: string;
  max?: string;
  required?: boolean;
  /** Extra ids for aria-describedby (field hint / outer error). */
  describedBy?: string;
  /** The value is valid but the form rejects it (e.g. "to" before "from"). */
  invalid?: boolean;
  /** Initial focus inside a dialog. */
  autoFocus?: boolean;
  /** Accessible name of the calendar button's field (defaults to the visible label via aria-labelledby on the input). */
  label?: string;
}) {
  const { t, lang } = useI18n();
  const [text, setText] = useState(() => formatDateInput(value, lang));
  const [touched, setTouched] = useState(false);
  const native = useRef<HTMLInputElement>(null);
  const check = checkDateInput(text, { required, min, max });

  // Follow the value from outside (filter reset, picker) unless the typed text already says it.
  useEffect(() => {
    setText((cur) => (checkDateInput(cur).iso === value ? cur : formatDateInput(value, lang)));
    // Deliberately not depending on `lang`: the next effect handles a language switch.
  }, [value]);
  // Language switch: show a valid date in the new language's form.
  useEffect(() => {
    setText((cur) => {
      const iso = checkDateInput(cur).iso;
      return iso ? formatDateInput(iso, lang) : cur;
    });
  }, [lang]);

  const commit = (next: string) => {
    const c = checkDateInput(next, { required, min, max });
    if (c.issue === null && c.iso !== null && c.iso !== value) onChange(c.iso);
  };

  // Typing only edits the text: half-typed or mid-correction dates (backspace) never reach the parent,
  // so filters do not reload and URLs do not change until the user is done (blur or Enter).
  const onType = (next: string) => setText(next);

  const onBlur = () => {
    setTouched(true);
    // An emptied required field goes back to its value; a valid one is shown in the canonical form.
    if (required && text.trim() === "") return setText(formatDateInput(value, lang));
    commit(text);
    if (check.issue === null && check.iso) setText(formatDateInput(check.iso, lang));
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setTouched(true);
      // Enter may also submit the surrounding form: apply the value synchronously so the submit sees it.
      flushSync(() => commit(text));
    }
  };

  const openPicker = () => {
    try {
      if (canShowPicker) native.current?.showPicker();
      else native.current?.focus(); // touch without showPicker: the overlaid native input takes the tap
    } catch {
      // Picker blocked (e.g. not user-activated); typing still works.
    }
  };

  const issue = touched ? check.issue : null;
  const fmtId = `${id}-format`;
  const errId = `${id}-date-error`;
  const describe = [describedBy, fmtId, issue ? errId : null].filter(Boolean).join(" ");
  const bound = (iso: string | undefined) => formatDateInput(iso, lang);

  return (
    <>
      <span className="date-field">
        <input
          id={id}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          spellCheck={false}
          placeholder={datePattern(lang)}
          value={text}
          maxLength={10}
          aria-invalid={issue || invalid ? true : undefined}
          aria-describedby={describe}
          aria-required={required || undefined}
          onChange={(e) => onType(e.target.value)}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          {...(autoFocus ? { "data-autofocus": true } : {})}
        />
        {(canShowPicker || touchPicker) && (
          <>
            <button type="button" className="date-field-button" onClick={openPicker} title={t("date.openPicker")} aria-label={label ? `${t("date.openPicker")}: ${label}` : t("date.openPicker")}>
              <Icon name="calendar" size={16} />
            </button>
            <input
              ref={native}
              type="date"
              className={`date-field-native${touchPicker ? " is-touch" : ""}`}
              tabIndex={-1}
              aria-hidden="true"
              value={value}
              min={min}
              max={max}
              onChange={(e) => {
                setTouched(true);
                setText(formatDateInput(e.target.value, lang));
                commit(e.target.value);
              }}
            />
          </>
        )}
        <span id={fmtId} className="sr-only">
          {t("date.format", { pattern: datePattern(lang) })}
        </span>
      </span>
      {issue && (
        <span id={errId} className="field-error date-field-error" role="alert">
          {t(ISSUE_KEY[issue], { pattern: datePattern(lang), min: bound(min), max: bound(max) })}
        </span>
      )}
    </>
  );
}
